/**
 * The equipment criticality of a contract's lines (procurement surveillance, wave INST
 * ruling 11): the current rating of each line, and the form that rates one more.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * A rating names its equipment line (the requisition item or tag): one current rating per
 * line of a contract, so a double submit and a genuine second line are told apart by the
 * line, not by the note. The same line again is the server's 409
 * criticality.line_already_rated naming the existing rating, said inside the dialog. The
 * total and the level are the server's; the form sends one whole score per element.
 */

import { useRef, useState } from "react";

import { ApiError } from "../api/client";
import {
  createRating,
  listRatings,
  readRatingScheme,
  type CriticalityRating,
  type RatingScheme,
} from "../api/criticality";
import { strings } from "../brand/strings";
import { ServerField } from "../lib/serverErrors";
import { EmptyState, Section, SkeletonRows, day } from "../screens/parts";
import { useQuery } from "../screens/useQuery";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { ActionDialog } from "./ActionDialog";

const copy = strings.criticality;

/** The register's columns: what each cell says in plain words. */
const RATING_COLUMNS: readonly RegisterColumn<CriticalityRating>[] = [
  {
    id: "rating",
    label: copy.columnRating,
    text: (row) => `${row.humanId} ${copy.revision(row.revision)}`,
    className: "font-mono whitespace-nowrap",
  },
  {
    id: "line",
    label: copy.columnLine,
    text: (row) => row.lineReference ?? copy.noLine,
  },
  { id: "type", label: copy.columnType, text: (row) => row.equipmentTypeLabel, filter: true },
  {
    id: "level",
    label: copy.columnLevel,
    text: (row) => `${row.levelLabel} (${String(row.total)})`,
    sortValue: (row) => row.total,
  },
  {
    id: "rated",
    label: copy.columnRated,
    text: (row) => `${row.ratedByName ?? ""} ${day(row.ratedAt.slice(0, 10))}`.trim(),
    sortValue: (row) => row.ratedAt,
  },
];

export function CriticalityRatingsBlock({
  token,
  contractId,
  projectId,
  onRated,
}: {
  readonly token: string;
  readonly contractId: string;
  /** The project the contract is performed under; a rating names both. */
  readonly projectId: string | null;
  readonly onRated: (message: string) => void;
}): React.JSX.Element {
  const [stamp, setStamp] = useState(0);
  const [rating, setRating] = useState(false);
  const ratings = useQuery(
    (signal) => listRatings(token, contractId, signal),
    [token, contractId, stamp],
  );

  return (
    <Section heading={copy.heading} lede={copy.lede}>
      <div data-testid="criticality-ratings">
        {ratings.kind === "loading" && <SkeletonRows rows={3} columns={5} />}
        {(ratings.kind === "failed" || ratings.kind === "absence") && (
          <div className="q-refusal" role="alert" data-testid="criticality-failed">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
        {ratings.kind === "ready" && ratings.data.items.length === 0 && (
          <EmptyState title={copy.emptyTitle} body={copy.emptyBody} />
        )}
        {ratings.kind === "ready" && ratings.data.items.length > 0 && (
          <DataTable
            data={ratings.data.items}
            columns={RATING_COLUMNS}
            caption={copy.heading}
            testId="criticality"
            getRowId={(row) => row.id}
            getRowTestId={() => "criticality-row"}
          />
        )}
        <div className="q-actions-row q-gap-top">
          <button
            type="button"
            className="q-button q-button--quiet"
            data-testid="criticality-rate-open"
            disabled={projectId === null}
            title={projectId === null ? copy.noProject : undefined}
            onClick={() => {
              setRating(true);
            }}
          >
            {copy.rateOpen}
          </button>
          {projectId === null && (
            <span className="q-note" data-testid="criticality-no-project">
              {copy.noProject}
            </span>
          )}
        </div>
      </div>
      {rating && projectId !== null && (
        <RateLineDialog
          token={token}
          contractId={contractId}
          projectId={projectId}
          onClose={() => {
            setRating(false);
          }}
          onRated={(rated) => {
            setRating(false);
            setStamp((one) => one + 1);
            onRated(copy.rated(rated.humanId, rated.lineReference ?? "", rated.levelLabel));
          }}
        />
      )}
    </Section>
  );
}

/**
 * Rate one equipment line: its line reference (required), its equipment type and one
 * whole score per element of the tenant's scheme. The refusal stays inside the dialog.
 */
export function RateLineDialog({
  token,
  contractId,
  projectId,
  onClose,
  onRated,
}: {
  readonly token: string;
  readonly contractId: string;
  readonly projectId: string;
  readonly onClose: () => void;
  readonly onRated: (rating: CriticalityRating) => void;
}): React.JSX.Element {
  const scheme = useQuery((signal) => readRatingScheme(token, signal), [token]);
  const [line, setLine] = useState("");
  const [equipmentTypeId, setEquipmentTypeId] = useState("");
  const [scores, setScores] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const sending = useRef(false);
  const ready: RatingScheme | null = scheme.kind === "ready" ? scheme.data : null;
  const lineRefused = error?.problem?.rule === "criticality.line_already_rated";

  const missing = [
    line.trim() === "" ? copy.lineLabel : null,
    equipmentTypeId === "" ? copy.typeLabel : null,
    ...(ready?.elements ?? [])
      .filter((one) => !/^\d+$/u.test(scores[one.code]?.trim() ?? ""))
      .map((one) => one.label),
  ].filter((one): one is string => one !== null);

  async function send(): Promise<void> {
    if (ready === null || sending.current) {
      return;
    }
    sending.current = true;
    setBusy(true);
    setError(null);
    try {
      const rated = await createRating(token, {
        project_id: projectId,
        contract_id: contractId,
        equipment_type_id: equipmentTypeId,
        scores: Object.fromEntries(
          ready.elements.map((one) => [one.code, Number(scores[one.code]?.trim() ?? "")]),
        ),
        note: note.trim() === "" ? null : note.trim(),
        line_reference: line.trim(),
      });
      onRated(rated);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }

  return (
    <ActionDialog open title={copy.rateHeading} onClose={onClose} error={error}>
      <p className="q-shell__lede">{copy.rateLede}</p>
      {scheme.kind === "loading" && <SkeletonRows rows={3} columns={2} />}
      {scheme.kind !== "loading" && scheme.kind !== "ready" && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{strings.common.failed}</p>
        </div>
      )}
      {ready !== null && (
        <>
          <div className="q-field">
            <label className="q-field__label" htmlFor="rate-line">
              {copy.lineLabel}
            </label>
            <p className="q-note" id="rate-line-hint">
              {copy.lineHint}
            </p>
            <ServerField field="line_reference">
              <input
                id="rate-line"
                className="q-input"
                data-testid="rate-line"
                aria-required="true"
                aria-invalid={lineRefused ? true : undefined}
                aria-describedby="rate-line-hint"
                value={line}
                onChange={(event) => {
                  setLine(event.target.value);
                }}
              />
            </ServerField>
          </div>
          <div className="q-field">
            <label className="q-field__label" htmlFor="rate-type">
              {copy.typeLabel}
            </label>
            <ServerField field="equipment_type_id">
              <select
                id="rate-type"
                className="q-input"
                data-testid="rate-type"
                value={equipmentTypeId}
                onChange={(event) => {
                  setEquipmentTypeId(event.target.value);
                }}
              >
                <option value="">{copy.typeChoose}</option>
                {ready.equipmentTypes.map((one) => (
                  <option key={one.id} value={one.id}>
                    {one.label}
                  </option>
                ))}
              </select>
            </ServerField>
          </div>
          {ready.elements.length === 0 ? (
            <p className="q-note" data-testid="rate-no-scheme">
              {copy.noScheme}
            </p>
          ) : (
            <ServerField field="scores">
              <fieldset id="rate-scores" className="q-fieldset q-subform" data-testid="rate-scores">
                <legend className="q-field__label">{copy.scoresLabel}</legend>
                <div className="q-form-grid">
                  {ready.elements.map((one) => (
                    <div className="q-field" key={one.code}>
                      <label className="q-field__label" htmlFor={`rate-score-${one.code}`}>
                        {copy.scoreLabel(one.label, one.scaleMax)}
                      </label>
                      <Input
                        id={`rate-score-${one.code}`}
                        data-testid={`rate-score-${one.code}`}
                        type="number"
                        inputMode="numeric"
                        min="1"
                        max={String(one.scaleMax)}
                        step="1"
                        value={scores[one.code] ?? ""}
                        onChange={(event) => {
                          setScores({ ...scores, [one.code]: event.target.value });
                        }}
                      />
                    </div>
                  ))}
                </div>
              </fieldset>
            </ServerField>
          )}
          <div className="q-field">
            <label className="q-field__label" htmlFor="rate-note">
              {copy.noteLabel}
            </label>
            <ServerField field="note">
              <textarea
                id="rate-note"
                className="q-textarea"
                data-testid="rate-note"
                value={note}
                onChange={(event) => {
                  setNote(event.target.value);
                }}
              />
            </ServerField>
          </div>
          {missing.length > 0 && (
            <p className="q-note" data-testid="rate-missing">
              {copy.missing(missing)}
            </p>
          )}
        </>
      )}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="rate-send"
          disabled={busy || ready === null || ready.elements.length === 0 || missing.length > 0}
          onClick={() => {
            void send();
          }}
        >
          {copy.rateSend}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}
